import { isLocalhost } from '../isEnv';
import { tc } from '../trackingContext';

const WAIT_BEFORE_RELOAD_MS = 500;
const CONSENT_TIMEOUT_MS = 5000;

// Policy name for implied consent states (CA, FL, PA)
const IMPLIED_CONSENT_POLICY_NAME = 'California, Florida, and Pennsylvania Request Policy';
const IMPLIED_CONSENT_DELAY_MS = 15000;

type ConsentAction = 'essential_only' | 'accept_all' | 'custom' | 'GPC' | 'DNT' | 'page_load';

interface CookieOption {
  gtm_key: string;
  isEnabled: boolean;
}

interface ConsentPreferences {
  consentPreferences?: {
    isCustomised: boolean;
    cookieOptions: CookieOption[];
  };
  action: ConsentAction;
  uniqueId: string;
  policyName: string;
  localeCode: string;
}

// Helper to check if marketing consent is enabled
function isMarketingEnabled(preferences: ConsentPreferences): boolean {
  const cookieOptions = preferences?.consentPreferences?.cookieOptions;
  if (!cookieOptions || !Array.isArray(cookieOptions)) {
    return false;
  }
  const marketingOption = cookieOptions.find(
    option => option.gtm_key === 'dg-category-marketing' || option.gtm_key === 'marketing'
  );
  return marketingOption?.isEnabled ?? false;
}

// Check if user is in an implied consent state (CA, FL, PA)
function checkImpliedConsentState(preferences: ConsentPreferences): boolean {
  return preferences?.policyName === IMPLIED_CONSENT_POLICY_NAME;
}

// Show implied consent notice banner for CA/FL/PA states
// Returns a promise that resolves when the notice is dismissed by timeout
function showImpliedConsentNotice(): Promise<void> {
  return new Promise(resolve => {
    if (sessionStorage.getItem('impliedConsentNoticeDismissed')) {
      resolve();
      return;
    }

    const dismissNotice = () => {
      if (window.DG_BANNER_API?.hideConsentBanner) {
        window.DG_BANNER_API.hideConsentBanner();
      }
      sessionStorage.setItem('impliedConsentNoticeDismissed', 'true');
      resolve();
    };

    setTimeout(dismissNotice, IMPLIED_CONSENT_DELAY_MS);
  });
}

export function determinePolicy(): Promise<{ policy: string[] }> {
  if (isLocalhost()) return Promise.resolve({ policy: ["don't load tags"] });

  return new Promise(resolve => {
    window.dgEvent = window.dgEvent || [];

    window.dgEvent.push({
      event: 'initial_preference_callback',
      params: async (preferences: ConsentPreferences) => {
        convertMarkdownLinks();
        if (preferences?.policyName) applyLayoutStyles(preferences.policyName);

        if (!preferences) {
          resolve({ policy: ["don't load tags"] });
          return;
        }

        // Check if user is in an implied consent US region (CA, FL, PA)
        if (checkImpliedConsentState(preferences)) {
          await showImpliedConsentNotice();
          resolve({ policy: ['load tags'] });
          return;
        }

        // For other regions: check marketing consent as usual
        const hasMarketingConsent = isMarketingEnabled(preferences);

        if (hasMarketingConsent) {
          resolve({ policy: ['load tags'] });
        } else {
          resolve({ policy: ["don't load tags"] });
        }
      }
    });

    // Fallback: if no preferences are found within timeout, default to no tags
    setTimeout(() => {
      resolve({ policy: ["don't load tags"] });
    }, CONSENT_TIMEOUT_MS);
  });
}

// Base styles shared across all layouts
const BASE_STYLES = `
  :host(.dg-consent-banner) {
    --dg-primary-font: "Moda Operandi Serif";
    --dg-secondary-font: "Moda Operandi Sans";
    --dg-consent-background-color: rgb(255, 255, 255);
    --dg-consent-background-border: none;
    --consent-border-radius: 0;
    --dg-button-font-size: 0.9em;
    --dg-button-font-weight: normal;
    --dg-button-color: rgb(25, 26, 27);
    --dg-button-background: rgb(255, 255, 255);
    --dg-button-border: 1px solid transparent;
    --dg-button-radius: 0;
    --dg-title-font-size: 1em;
    --dg-body-font-size: 0.8em;
  }

  @media (max-width: 768px) {
    :host(.dg-consent-banner) {
      --dg-seperator-padding-mobile: 0;
      --dg-default-margin-mobile: 0;
    }
  }

  :host(.dg-consent-banner) .dg-button {
    text-transform: uppercase !important;
    margin: 4px !important;
    padding: 8px 16px !important;
    min-width: 9em !important;
    font-family: "Moda Operandi Sans" !important;
  }

  :host(.dg-consent-banner) .dg-button.accept_all {
    --dg-button-color: rgb(255, 255, 255);
    --dg-button-background: rgb(25, 26, 27);
  }

  :host(.dg-consent-banner) .dg-link {
    display: inline !important;
    margin: 0 !important;
    padding: 0 !important;
    line-height: inherit !important;
    font-size: inherit !important;
    vertical-align: baseline !important;
  }
`;

// Modal-specific overrides
const MODAL_STYLES = `
  :host(.dg-consent-banner.dg-modal) .dg-app {
    flex-direction: row !important;
    flex-wrap: wrap !important;
  }

  :host(.dg-consent-banner.dg-modal) .dg-app > :not(.dg-button) {
    flex: 0 0 100% !important;
  }

  :host(.dg-consent-banner.dg-modal) .dg-app > .dg-button:first-of-type {
    margin-left: auto !important;
  }

  :host(.dg-consent-banner.dg-modal) .dg-button {
    margin-left: 0 !important;
  }

  :host(.dg-consent-banner.dg-modal) .dg-header-close {
    position: absolute;
    top: 1em;
    right: 1em;
  }

  @media (max-width: 768px) {
    :host(.dg-consent-banner.dg-modal) .dg-app > .dg-button:first-of-type {
      margin-left: 0 !important;
    }

    :host(.dg-consent-banner.dg-modal) .dg-button {
      width: 100% !important;
    }
  }
`;

// Layout-specific styles keyed by policy name
const LAYOUT_STYLES: Record<string, string> = {
  [IMPLIED_CONSENT_POLICY_NAME]: `
    :host(.dg-consent-banner) .dg-app {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      padding: 0.5em 1em;
      gap: 0em;
    }

    :host(.dg-consent-banner) .dg-header {
      display: flex;
      justify-content: space-between;
      width: 100%;
    }

    :host(.dg-consent-banner) .dg-text-title {
      margin: 8px 0 0;
    }

    :host(.dg-consent-banner) .dg-header-buttons {
      position: absolute;
      right: 1em;
      top: 50%;
      transform: translateY(-50%);
    }
  `
};

const DEFAULT_LAYOUT_STYLES = `
  :host(.dg-consent-banner) .dg-app {
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 1em;
  }

  :host(.dg-consent-banner) .dg-button.accept_all {
    margin-left: auto !important;
  }

  @media (max-width: 768px) {
    :host(.dg-consent-banner) .dg-app {
      flex-direction: column !important;
      align-items: stretch !important;
      padding: 1em !important;
      gap: 0.5em !important;
    }

    :host(.dg-consent-banner) .dg-button {
      width: 100% !important;
      margin-left: 0 !important;
      margin-right: 0 !important;
    }

    :host(.dg-consent-banner) .dg-button.accept_all {
      margin-left: 0 !important;
    }
  }
`;

// Update the dg-consent-custom-style tag with base + layout-specific styles.
// DataGrail automatically picks up this tag and applies it inside its Shadow DOM.
function applyLayoutStyles(policyName: string) {
  const styleEl = document.getElementById('dg-consent-custom-style');
  if (!styleEl) return;

  const layoutCss = LAYOUT_STYLES[policyName] ?? DEFAULT_LAYOUT_STYLES;
  styleEl.textContent = BASE_STYLES + layoutCss + MODAL_STYLES;
}

const MARKDOWN_LINK_POLL_INTERVAL_MS = 100;
const MARKDOWN_LINK_MAX_ATTEMPTS = 50;

// Process text elements to convert markdown-style [text](url) links to anchor tags.
// Supports a special "layer:" scheme to open a specific DataGrail consent layer,
// e.g. [Cookie Consent Tool](layer:categories-layer) calls DG_BANNER_API.showLayer().
function processTextElements(root: ShadowRoot | Element) {
  const textElements = root.querySelectorAll('.dg-text-body, .dg-main-content-explanation');

  textElements.forEach(el => {
    if (!el.innerHTML.includes('](')) return;

    // Replace markdown links: layer: scheme gets a data attribute, regular links get href
    el.innerHTML = el.innerHTML.replace(
      /\[([^\]]+)\]\(([^)]+)\)/g,
      (_match, text: string, url: string) => {
        if (url.startsWith('layer:')) {
          const layerId = url.slice('layer:'.length);
          return `<a href="#" data-dg-layer="${layerId}" class="dg-link">${text}</a>`;
        }
        return `<a href="${url}" class="dg-link">${text}</a>`;
      }
    );

    // Attach click handlers for layer links
    el.querySelectorAll<HTMLAnchorElement>('a[data-dg-layer]').forEach(link => {
      link.addEventListener('click', event => {
        event.preventDefault();
        const layerId = link.dataset.dgLayer;
        if (layerId) window.DG_BANNER_API?.showLayer(layerId);
      });
    });
  });
}

// Polls for the DataGrail banner shadow root, processes initial text elements,
// then observes for new content (e.g. when a new layer opens) to re-process links.
function convertMarkdownLinks(attempt = 0) {
  const banner = document.querySelector('.dg-consent-banner');
  const shadowRoot = banner?.shadowRoot;

  if (!shadowRoot && attempt < MARKDOWN_LINK_MAX_ATTEMPTS) {
    setTimeout(() => convertMarkdownLinks(attempt + 1), MARKDOWN_LINK_POLL_INTERVAL_MS);
    return;
  }
  if (!shadowRoot) return;

  // Process existing content
  processTextElements(shadowRoot);

  // Watch for new layers being rendered inside the shadow DOM
  const observer = new MutationObserver(() => processTextElements(shadowRoot));
  observer.observe(shadowRoot, { childList: true, subtree: true });
}

export function initializeDataGrail() {
  // Initialize dgEvent array (should be already initiatlized from SSR in server.tsx)
  window.dgEvent = window.dgEvent || [];

  // Create the dg-consent-custom-style tag that DataGrail picks up and
  // applies inside its Shadow DOM. Starts with base styles; layout-specific
  // styles get merged in by applyLayoutStyles() when the policy is known.
  const style = document.createElement('style');
  style.id = 'dg-consent-custom-style';
  style.textContent = BASE_STYLES;
  document.head.appendChild(style);

  // Set up preference change callback (fires when user updates preferences)
  window.dgEvent.push({
    event: 'preference_callback',
    params: (preferences: ConsentPreferences) => {
      // TODO: We could look into replacing page reload with dynamic tag loading/unloading for better UX
      // Current approach: reload page to apply new consent preferences
      // Future approach: dynamically enable/disable integrations without reload
      //   - Use window.analytics destination middleware to block/unblock integrations
      //   - Dynamically load/unload GA4 and Algolia based on new preferences
      //   - Update isInitialized flags for consent-aware wrappers

      // Track consent actions
      const trackingProps = {
        addToTrackingEvent: {
          action: preferences.action,
          policyName: preferences.policyName,
          localeCode: preferences.localeCode
        }
      };

      switch (preferences.action) {
        case 'accept_all':
          tc.track('Consent Given', trackingProps);
          window.setTimeout(() => window.location.reload(), WAIT_BEFORE_RELOAD_MS);
          break;
        case 'essential_only':
          tc.track('Decline Given', trackingProps);
          window.setTimeout(() => window.location.reload(), WAIT_BEFORE_RELOAD_MS);
          break;
        case 'custom':
          // User made custom selection
          if (isMarketingEnabled(preferences)) {
            tc.track('Consent Given', trackingProps);
          } else {
            tc.track('Consent Withdrawn', trackingProps);
          }
          window.setTimeout(() => window.location.reload(), WAIT_BEFORE_RELOAD_MS);
          break;
        default:
          // GPC or DNT
          tc.track('Consent Withdrawn', trackingProps);
          window.setTimeout(() => window.location.reload(), WAIT_BEFORE_RELOAD_MS);
      }
    }
  });
}
